import { useCallback, useEffect, useState } from "react";

import { useApi } from "@platform/hooks/useApi";

import type {
  InfraConfigUI,
  TargetHealthUI,
  UpsertInfraConfigRequest,
} from "../types";

interface UseInfraConfigReturn {
  infraConfig: InfraConfigUI | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/** Fetch infrastructure configuration for a project. */
export function useInfraConfig(
  projectAlias: string | undefined,
): UseInfraConfigReturn {
  const api = useApi();
  const [infraConfig, setInfraConfig] = useState<InfraConfigUI | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!projectAlias) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<InfraConfigUI>(
        `/api/v1/projects/${encodeURIComponent(projectAlias)}/infra-config`,
        {
          errorMessage: "Failed to load infrastructure config",
          showLoading: false,
        },
      );
      setInfraConfig(result);
    } catch {
      setError("Failed to load infrastructure config.");
    } finally {
      setLoading(false);
    }
    // `api` excluded — useApi() returns a new object every render, which
    // would retrigger the fetch effect infinitely. api.get is stable.
  }, [projectAlias]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { infraConfig, loading, error, reload };
}

interface UseTargetsHealthReturn {
  targets: TargetHealthUI[];
  loading: boolean;
  reload: () => void;
}

/** Fetch health status of infrastructure targets for a project. */
export function useTargetsHealth(
  projectAlias: string | undefined,
): UseTargetsHealthReturn {
  const api = useApi();
  const [targets, setTargets] = useState<TargetHealthUI[]>([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!projectAlias) return;
    setLoading(true);
    try {
      const result = await api.get<{ targets: TargetHealthUI[] }>(
        `/api/v1/projects/${encodeURIComponent(projectAlias)}/targets/health`,
        {
          errorMessage: "Failed to load targets health",
          showLoading: false,
        },
      );
      setTargets(result?.targets ?? []);
    } catch {
      setTargets([]);
    } finally {
      setLoading(false);
    }
    // `api` excluded — see useInfraConfig note. api.get is stable.
  }, [projectAlias]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { targets, loading, reload };
}

interface UseUpsertInfraConfigReturn {
  upsert: (
    projectAlias: string,
    dto: UpsertInfraConfigRequest,
  ) => Promise<InfraConfigUI | null>;
  isSubmitting: boolean;
  submitError: string | null;
}

/** Mutation hook for creating/updating infrastructure config. */
export function useUpsertInfraConfig(): UseUpsertInfraConfigReturn {
  const api = useApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const upsert = useCallback(
    async (
      projectAlias: string,
      dto: UpsertInfraConfigRequest,
    ): Promise<InfraConfigUI | null> => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const result = await api.post<InfraConfigUI, UpsertInfraConfigRequest>(
          `/api/v1/projects/${encodeURIComponent(projectAlias)}/infra-config`,
          dto,
          { errorMessage: "Failed to save infrastructure config" },
        );
        return result;
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : "Failed to save infrastructure config";
        setSubmitError(msg);
        return null;
      } finally {
        setIsSubmitting(false);
      }
      // `api` excluded — see useInfraConfig note. api.post is stable.
    },
    [],
  );

  return { upsert, isSubmitting, submitError };
}
